import React, { useRef, useEffect } from "react";
import { Container } from "@pixi/react";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { useSlot } from "../../../Contexts/SlotRegistryContext";
import { HOLD_AND_WIN_GAME_VIEW_SLOTS } from "../HoldAndWinGameView/HoldAndWinGameViewController";
import HoldAndWinCounterController from "./HoldAndWinCounterController";

/**
 * HoldAndWinCounter — Cleopatra's Respins counter.
 *
 * Reworked on PotCollectionRespinCounter: the controller lives on a container
 * that is injected into the room's `respin_counter_placeholder` slot through
 * the SlotRegistry, and is published on `holdAndWinRespinCounterRef`.
 *
 * Driven imperatively by HoldAndWinControlContext, not by a Redux effect —
 * the timing is the respin's (−1 as it starts, the server's value once it
 * lands):
 *   entry             showFull()          0 → 3
 *   reload            setValue(n)         no animation
 *   respin starts     playAnimation(n−1)
 *   respin landed     playAnimation(server value)
 */
const HoldAndWinCounter = () => {
  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const { holdAndWinRespinCounterRef } = useReelRefs();
  const slot = useSlot(HOLD_AND_WIN_GAME_VIEW_SLOTS.RESPIN_COUNTER);

  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      controllerRef.current = new HoldAndWinCounterController(containerRef.current);
      holdAndWinRespinCounterRef.current = controllerRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Into the room's slot — the slot places and scales it.
  useEffect(() => {
    if (!slot || !containerRef.current) return undefined;
    const node = containerRef.current;
    slot.addChild(node);
    return () => {
      if (node.parent === slot) slot.removeChild(node);
    };
  }, [slot]);

  useEffect(() => {
    return () => {
      controllerRef.current?.cleanup();
      controllerRef.current = null;
      holdAndWinRespinCounterRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container ref={containerRef} name="hold_and_win_respin_counter" sortableChildren />
  );
};

export default HoldAndWinCounter;
